/**
 * Stock Chart Viewer - UI Manager
 * Handles generic UI updates and state reflection
 */

import { elements } from './dom.js';
import { formatPrice, formatVolume } from './formatters.js';
import { state } from '../state.js';
import { initChart } from '../chart/init.js';
import { updateIndicators } from '../chart/indicators.js';
import { updateWatchlistButton } from '../features/watchlist.js';

export function showLoading() {
    if (elements.loadingOverlay) {
        elements.loadingOverlay.classList.remove('hidden');
    }
}

export function hideLoading() {
    if (elements.loadingOverlay) {
        elements.loadingOverlay.classList.add('hidden');
    }
}

export function showError(message) {
    if (!elements.errorText || !elements.errorMessage) return;

    elements.errorText.textContent = message;
    elements.errorMessage.classList.remove('hidden');

    // Clear any existing timeout (if we were tracking it) or just set a new one
    // Simple enough to just set new timeout for now
    setTimeout(() => {
        elements.errorMessage.classList.add('hidden');
    }, 5000);
}

export function hideError() {
    if (elements.errorMessage) {
        elements.errorMessage.classList.add('hidden');
    }
}

export function showToast(message, type = 'info') {
    if (!elements.toastContainer) return;

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;

    // Create removing animation
    const remove = () => {
        toast.classList.add('fade-out');
        toast.addEventListener('animationend', () => {
            if (toast.parentElement) {
                toast.parentElement.removeChild(toast);
            }
        });
    };

    // Auto remove after 3 seconds
    setTimeout(remove, 3000);

    // Click to remove immediately
    toast.addEventListener('click', remove);

    elements.toastContainer.appendChild(toast);
}

export function updateUI(data) {
    // Show sections
    elements.stockInfo.classList.remove('hidden');
    elements.statsPanel.classList.remove('hidden');
    if (elements.welcomeMessage) {
        elements.welcomeMessage.classList.add('hidden');
    }

    // Update Header
    elements.companyName.textContent = data.company;
    elements.tickerSymbol.textContent = data.symbol;
    elements.currentPrice.textContent = formatPrice(data.currentPrice);

    const change = data.change || 0;
    const changePercent = data.changePercent || 0;
    const changeText = `${change >= 0 ? '+' : ''}${formatPrice(change)} (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%)`;
    elements.priceChange.textContent = changeText;
    elements.priceChange.className = `change ${change >= 0 ? 'positive' : 'negative'}`;

    if (data.cachedAt) {
        const cachedDate = new Date(data.cachedAt);
        elements.cacheIndicator.textContent = `📦 Cached ${cachedDate.toLocaleDateString()}`;
        elements.cacheIndicator.classList.remove('hidden');
    } else {
        elements.cacheIndicator.classList.add('hidden');
    }

    // Update Stats
    elements.statOpen.textContent = formatPrice(data.open);
    elements.statHigh.textContent = formatPrice(data.high);
    elements.statLow.textContent = formatPrice(data.low);
    elements.statVolume.textContent = formatVolume(data.volume);

    // Update Chart
    initChart();
    if (state.candleSeries && data.candleData) {
        state.candleSeries.setData(data.candleData);
    }
    if (state.volumeSeries && data.volumeData) {
        state.volumeSeries.setData(data.volumeData);
    }
    if (state.chart) {
        state.chart.timeScale().fitContent();
    }

    updateIndicators();
    updateWatchlistButton();
}

export function updatePeriodButtons() {
    elements.periodBtns.forEach(btn => {
        btn.classList.toggle('active', btn.dataset.period === state.currentPeriod);
    });
}

export function updateHeaderCacheMode() {
    if (elements.headerCacheMode) {
        if (state.cacheOnlyMode) {
            elements.headerCacheMode.classList.remove('hidden');
            elements.headerCacheMode.title = 'キャッシュ専用モード: ON';
        } else {
            elements.headerCacheMode.classList.add('hidden');
        }
    }
}

export function updateFooterInfo(provider, limit) {
    const providerSpan = document.getElementById('footer-provider');
    const limitSpan = document.getElementById('footer-limit');
    const link = document.getElementById('footer-api-link');

    if (providerSpan) {
        if (provider === 'alphavantage') providerSpan.textContent = 'Alpha Vantage';
        else if (provider === 'finnhub') providerSpan.textContent = 'Finnhub';
        else if (provider === 'twelvedata') providerSpan.textContent = 'Twelve Data';
        else providerSpan.textContent = provider || 'Unknown';
    }

    if (limitSpan) {
        limitSpan.textContent = limit;
    }

    if (link) {
        if (provider === 'alphavantage') link.href = 'https://www.alphavantage.co/support/#api-key';
        else if (provider === 'finnhub') link.href = 'https://finnhub.io/dashboard';
        else if (provider === 'twelvedata') link.href = 'https://twelvedata.com/dashboard';
    }
}
